import React, { useEffect, useMemo, useState } from "react";
import NavigationHeader from "../../../components/ui/NavigationHeader";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Icon from "../../../components/AppIcon";
import { baseFilterOptions, formatBaseLabel } from "../../../utils/baseDisplay";
import { fetchCashFlowAnalysis } from "../services/financeiroApi";

const firstDayOfMonthISO = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
const formatNumber = (value, digits = 0) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(value || 0));
const formatDateBR = (value) => {
  if (!value) return "---";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};
const normalizeMoneyInput = (value) => Number(String(value || "0").replace(/\./g, "").replace(",", ".")) || 0;

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f7efe2] text-[#3b1d0f]">
      <NavigationHeader />
      <main className="px-4 pb-10 pt-24 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}

function MetricCard({ label, value, helper, icon }) {
  return (
    <div className="rounded-2xl border border-[#ead8c2] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[#7a5c46]">{label}</div>
          <div className="mt-2 text-2xl font-bold text-[#3b1d0f]">{value}</div>
          {helper ? <div className="mt-2 text-sm text-[#9a806c]">{helper}</div> : null}
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff3df] text-[#d97706]"><Icon name={icon} size={18} /></div>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, icon, children }) {
  return (
    <section className="rounded-[24px] border border-[#ead8c2] bg-white shadow-sm overflow-hidden">
      <div className="border-b border-[#efe2d1] px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff3df] text-[#d97706]"><Icon name={icon} size={18} /></div>
          <div>
            <h2 className="text-xl font-semibold text-[#3b1d0f]">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-[#7a5c46]">{subtitle}</p> : null}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Table({ rows, columns, emptyMessage }) {
  if (!rows?.length) return <div className="rounded-2xl border border-dashed border-[#ead8c2] bg-[#fffaf4] px-4 py-8 text-center text-sm text-[#7a5c46]">{emptyMessage}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead><tr className="border-b border-[#efe2d1] text-left text-[#8b6a52]">{columns.map((column) => <th key={column.key} className="px-3 py-3 font-semibold">{column.label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={row.data || row.codigo || index} className="border-b border-[#f4eadf] last:border-b-0">{columns.map((column) => <td key={column.key} className="px-3 py-3 align-top text-[#4f3426]">{column.render ? column.render(row) : row[column.key] ?? "---"}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

const initialFilters = {
  de: firstDayOfMonthISO(),
  ate: todayISO(),
  base: "A",
  dateType: "vencimento",
  limit: "1000",
};

export default function FluxoCaixaPage() {
  const [filters, setFilters] = useState(initialFilters);
  const saldoStorageKey = (base) => `financeiroFluxoSaldoInicial:${base === "B" ? "B" : "A"}`;
  const storedSaldo = (base) => normalizeMoneyInput(localStorage.getItem(saldoStorageKey(base)) || "0");
  const combinedStoredSaldo = () => storedSaldo("A") + storedSaldo("B");
  const [saldoInicialInput, setSaldoInicialInput] = useState(() => localStorage.getItem(saldoStorageKey(initialFilters.base)) || "0");
  const [data, setData] = useState({ summary: {}, daily: [], receivables: [], payables: [], warnings: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const saldoInicial = useMemo(() => normalizeMoneyInput(saldoInicialInput), [saldoInicialInput]);

  const changeCompany = (base) => {
    const nextBase = ["A", "B", "ALL"].includes(base) ? base : "A";
    if (filters.base !== "ALL") localStorage.setItem(saldoStorageKey(filters.base), String(saldoInicialInput || "0"));
    setFilters((prev) => ({ ...prev, base: nextBase }));
    setSaldoInicialInput(nextBase === "ALL" ? String(combinedStoredSaldo()) : localStorage.getItem(saldoStorageKey(nextBase)) || "0");
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const effectiveSaldoInicial = filters.base === "ALL" ? combinedStoredSaldo() : saldoInicial;
      if (filters.base !== "ALL") localStorage.setItem(saldoStorageKey(filters.base), String(saldoInicialInput || "0"));
      const response = await fetchCashFlowAnalysis({ ...filters, saldoInicial: effectiveSaldoInicial });
      if (filters.base === "ALL") setSaldoInicialInput(String(effectiveSaldoInicial));
      setData({
        summary: response?.summary || {},
        daily: Array.isArray(response?.daily) ? response.daily : [],
        receivables: Array.isArray(response?.receivables) ? response.receivables : [],
        payables: Array.isArray(response?.payables) ? response.payables : [],
        warnings: Array.isArray(response?.warnings) ? response.warnings : [],
      });
    } catch (err) {
      setError(err?.message || "Não foi possível carregar o fluxo de caixa.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const summary = data.summary || {};

  return (
    <PageShell>
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        <section className="rounded-[28px] border border-[#ead8c2] bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#ead8c2] bg-[#fffaf4] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#8b6a52]"><Icon name="ArrowLeftRight" size={14} /> Financeiro</span>
              <h1 className="mt-4 text-3xl font-bold text-[#3b1d0f]">Fluxo de Caixa</h1>
              <p className="mt-2 max-w-4xl text-sm leading-relaxed text-[#7a5c46]">Compara por dia as entradas de recebimentos com as saídas de pagamentos, considerando data de vencimento ou data de pagamento.</p>
            </div>
            <Button variant="outline" onClick={loadData} loading={loading} iconName="RefreshCw">Atualizar</Button>
          </div>
        </section>

        <Panel title="Configuração e filtros" subtitle={filters.base === "ALL" ? `Saldo e movimentos consolidados de ${formatBaseLabel(filters.base)}` : `Saldo e movimentos exclusivos de ${formatBaseLabel(filters.base)}`} icon="Settings2">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <Input
              label={filters.base === "ALL" ? "Saldo inicial consolidado" : "Saldo inicial"}
              description={filters.base === "ALL" ? "Soma automática dos saldos da Di Brownie e DSSO" : undefined}
              placeholder="Ex: 5000"
              value={saldoInicialInput}
              disabled={filters.base === "ALL"}
              onChange={(e) => setSaldoInicialInput(e.target.value)}
            />
            <Input label="De" type="date" value={filters.de} onChange={(e) => setFilters((prev) => ({ ...prev, de: e.target.value }))} />
            <Input label="Até" type="date" value={filters.ate} onChange={(e) => setFilters((prev) => ({ ...prev, ate: e.target.value }))} />
            <Select label="Empresa" value={filters.base} onChange={changeCompany} options={baseFilterOptions} />
            <Select label="Comparar por" value={filters.dateType} onChange={(value) => setFilters((prev) => ({ ...prev, dateType: value }))} options={[{ value: "vencimento", label: "Data de vencimento" }, { value: "pagamento", label: "Data de pagamento" }]} />
            <Input label="Limite por empresa" type="number" value={filters.limit} onChange={(e) => setFilters((prev) => ({ ...prev, limit: e.target.value }))} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={loadData} loading={loading} iconName="Search">Buscar fluxo</Button>
            <span className="text-sm text-[#7a5c46]">{filters.base === "ALL" ? "Na opção conjunta, entradas, saídas e saldo são consolidados. O saldo inicial é a soma dos valores cadastrados individualmente na Di Brownie e DSSO." : "Cada empresa mantém seu próprio saldo inicial. Exemplo: saldo R$ 5.000 + entradas R$ 3.000 - saídas R$ 2.000 = saldo final R$ 6.000."}</span>
          </div>
        </Panel>

        {error ? <div className="rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{error}</div> : null}
        {(data.warnings || []).length ? <div className="rounded-2xl border border-[#f7d7a6] bg-[#fff8ed] px-4 py-3 text-sm text-[#8a5a00]">Avisos: {(data.warnings || []).map((item) => `${formatBaseLabel(item.base)}: ${item.message}`).join(" | ")}</div> : null}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <MetricCard label="Saldo inicial" value={formatCurrency(summary.saldoInicial || 0)} helper="Configurado manualmente" icon="Banknote" />
          <MetricCard label="Entradas" value={formatCurrency(summary.totalEntradas || 0)} helper={`${formatNumber(summary.quantidadeRecebimentos || 0)} recebimentos`} icon="ArrowDownLeft" />
          <MetricCard label="Saídas" value={formatCurrency(summary.totalSaidas || 0)} helper={`${formatNumber(summary.quantidadePagamentos || 0)} pagamentos`} icon="ArrowUpRight" />
          <MetricCard label="Saldo do período" value={formatCurrency(summary.saldoPeriodo || 0)} helper="Entradas - saídas" icon="Calculator" />
          <MetricCard label="Saldo final" value={formatCurrency(summary.saldoFinal || 0)} helper="Saldo inicial + período" icon="Wallet" />
        </div>

        <Panel title="Comparação por dia" subtitle="Entrada - saída = saldo acumulado" icon="CalendarDays">
          <Table
            rows={data.daily || []}
            columns={[
              { key: "data", label: "Data", render: (row) => formatDateBR(row.data) },
              { key: "entrada", label: "Entrada", render: (row) => formatCurrency(row.entrada) },
              { key: "saida", label: "Saída", render: (row) => formatCurrency(row.saida) },
              { key: "saldoDia", label: "Saldo do dia", render: (row) => formatCurrency(row.saldoDia) },
              { key: "saldo", label: "Saldo acumulado", render: (row) => formatCurrency(row.saldo) },
              { key: "recebimentos", label: "Recebimentos", render: (row) => formatNumber(row.recebimentos) },
              { key: "pagamentos", label: "Pagamentos", render: (row) => formatNumber(row.pagamentos) },
            ]}
            emptyMessage={loading ? "Buscando recebimentos e pagamentos..." : "Nenhum movimento encontrado no período."}
          />
        </Panel>
      </div>
    </PageShell>
  );
}
